/**
 * VehicleModalTabs - Abas do modal premium de veículo
 *
 * Reaproveita o mesmo padrão visual das abas do modal de cliente
 * (barra de 36px, ícone + rótulo + contador e indicador ativo).
 *
 * @version 1.8.0
 * RELEASE: 2026-10-09 v1.8.0 - Barra de abas passa a usar ModalTabsOverflowBar:
 *            abas excedentes ficam no botão "..." (sem barra de rolagem horizontal)
 * RELEASE: 2026-10-09 v1.7.0 - Contador na aba Equipamentos e nova aba "O.S" (Ordens de Serviço do veículo)
 * RELEASE: 2026-10-02 v1.6.0 - Nova aba "Fluxos" (Workflow com raiz Veículo)
 * RELEASE: 2026-09-26 v1.5.0 - Nova aba "Vistoria" (módulo Checklist)
 * RELEASE: 2026-08-18 v1.3.0 - Nova aba "Financeiro" (cobranças/assinaturas do veículo)
 * RELEASE: 2026-08-08 v1.0.0 - Criação inicial
 */

import {
  LayoutDashboard,
  Briefcase,
  FileSignature,
  ListChecks,
  MessageSquare,
  ReceiptText,
  CreditCard,
  Paperclip,
  History,
  ArrowLeftRight,
  ClipboardCheck,
  Workflow,
  Cpu,
  Wrench,
} from 'lucide-react';
import { ModalTabsOverflowBar } from '@/components/common/ModalTabsOverflowBar';

/** Abas disponíveis no modal de veículo (v1.4.0: aba Transferências) */
export type VehicleModalTab =
  | 'summary'
  | 'deals'
  | 'contracts'
  | 'tasks'
  | 'chat'
  | 'fines'
  | 'financeiro'
  | 'attachments'
  | 'transfers'
  | 'inspections'
  | 'workflow'
  // 2026-10-06: equipamentos instalados (módulo Estoque)
  | 'equipment'
  // 2026-10-09: ordens de serviço do veículo
  | 'service_orders'
  | 'history';


interface VehicleModalTabsProps {
  activeTab: VehicleModalTab;
  onTabChange: (tab: VehicleModalTab) => void;
  dealsCount?: number;
  contractsCount?: number;
  tasksCount?: number;
  /** Total de conversas do contato vinculado (aba Chat) */
  chatCount?: number;
  finesCount?: number;
  notesCount?: number;
  /** Total de anexos do veículo (aba Anexos) */
  attachmentsCount?: number;
  /** v1.3.0: total de documentos financeiros do veículo */
  financeiroCount?: number;
  /** v1.4.0: total de períodos de posse (aba Transferências) */
  transfersCount?: number;
  /** v1.6.0: vistorias em aberto (aba Vistoria) */
  inspectionsCount?: number;
  /** v1.7.0: equipamentos instalados agora */
  equipmentCount?: number;
  /** v1.7.0: total de OS do veículo */
  serviceOrdersCount?: number;
  /** v1.7.0: aba O.S (módulo OS + permissão os_list) */
  serviceOrdersEnabled?: boolean;
  /** Contato vinculado (habilita a aba Chat) */
  hasContact?: boolean;
  /** Módulo de tarefas habilitado no tenant */
  tasksEnabled?: boolean;
  /** Permissão/módulo de multas */
  finesEnabled?: boolean;
  /** Permissão de visualizar contratos */
  canViewContracts?: boolean;
  /** v1.2.0: permissão de visualizar negócios (menu crm_deals) */
  canViewDeals?: boolean;
  /** v1.3.0: aba Financeiro (módulo financeiro habilitado + permissão) */
  financeiroEnabled?: boolean;
  /** v1.5.0: aba Vistoria (módulo Checklist habilitado) */
  /** 2026-10-06: aba Equipamentos (módulo Estoque + permissão de Movimentações) */
  equipmentEnabled?: boolean;
  inspectionsEnabled?: boolean;
  /** v1.6.0: aba Fluxos (módulo Workflow habilitado) */
  workflowEnabled?: boolean;
}

const tabsConfig: Array<{
  id: VehicleModalTab;
  label: string;
  icon: typeof LayoutDashboard;
  countKey?:
    | 'dealsCount'
    | 'contractsCount'
    | 'tasksCount'
    | 'chatCount'
    | 'finesCount'
    | 'notesCount'
    | 'attachmentsCount'
    | 'financeiroCount'
    | 'transfersCount'
    | 'inspectionsCount'
    | 'equipmentCount'
    | 'serviceOrdersCount';
}> = [
  { id: 'summary', label: 'Resumo', icon: LayoutDashboard },
  { id: 'deals', label: 'Negócios', icon: Briefcase, countKey: 'dealsCount' },
  { id: 'contracts', label: 'Contratos', icon: FileSignature, countKey: 'contractsCount' },
  { id: 'tasks', label: 'Tarefas', icon: ListChecks, countKey: 'tasksCount' },
  { id: 'chat', label: 'Chat', icon: MessageSquare, countKey: 'chatCount' },
  { id: 'fines', label: 'Multas', icon: ReceiptText, countKey: 'finesCount' },
  { id: 'financeiro', label: 'Financeiro', icon: CreditCard, countKey: 'financeiroCount' },
  { id: 'attachments', label: 'Anexos', icon: Paperclip, countKey: 'attachmentsCount' },
  // v1.4.0: histórico de proprietários (ledger de posse)
  { id: 'transfers', label: 'Transferências', icon: ArrowLeftRight, countKey: 'transfersCount' },
  // v1.5.0: vistorias do veículo (módulo Checklist)
  { id: 'inspections', label: 'Vistoria', icon: ClipboardCheck, countKey: 'inspectionsCount' },
  // v1.6.0: processos do Workflow do veículo
  { id: 'workflow', label: 'Fluxos', icon: Workflow },
  // 2026-10-06: kits/rastreadores/chips instalados (vários por veículo)
  { id: 'equipment', label: 'Equipamentos', icon: Cpu, countKey: 'equipmentCount' },
  { id: 'service_orders', label: 'O.S', icon: Wrench, countKey: 'serviceOrdersCount' },
  { id: 'history', label: 'Histórico', icon: History },
];

/** Barra de abas (36px) */
export function VehicleModalTabs({
  activeTab,
  onTabChange,
  dealsCount,
  contractsCount,
  tasksCount,
  chatCount,
  finesCount,
  notesCount,
  attachmentsCount,
  financeiroCount,
  transfersCount,
  inspectionsCount,
  equipmentCount,
  serviceOrdersCount,
  serviceOrdersEnabled = false,
  hasContact = false,
  tasksEnabled = true,
  finesEnabled = false,
  canViewContracts = true,
  canViewDeals = true,
  financeiroEnabled = false,
  inspectionsEnabled = false,
  workflowEnabled = false,
  equipmentEnabled = false,
}: VehicleModalTabsProps) {
  const counts = {
    dealsCount,
    contractsCount,
    tasksCount,
    chatCount,
    finesCount,
    notesCount,
    attachmentsCount,
    financeiroCount,
    transfersCount,
    inspectionsCount,
    equipmentCount,
    serviceOrdersCount,
  };


  const visibleTabs = tabsConfig.filter((tab) => {
    if (tab.id === 'chat' && !hasContact) return false;
    if (tab.id === 'tasks' && !tasksEnabled) return false;
    if (tab.id === 'fines' && !finesEnabled) return false;
    if (tab.id === 'contracts' && !canViewContracts) return false;
    // v1.2.0: aba Negócios respeita permissão do menu crm_deals
    if (tab.id === 'deals' && !canViewDeals) return false;
    // v1.3.0: aba Financeiro depende do módulo financeiro e da permissão
    if (tab.id === 'financeiro' && !financeiroEnabled) return false;
    if (tab.id === 'inspections' && !inspectionsEnabled) return false;
    if (tab.id === 'workflow' && !workflowEnabled) return false;
    if (tab.id === 'equipment' && !equipmentEnabled) return false;
    if (tab.id === 'service_orders' && !serviceOrdersEnabled) return false;
    return true;
  });

  // v1.8.0: renderização delegada ao componente compartilhado com overflow "..."
  // (abas que não cabem na largura ficam na lista suspensa, sem barra de rolagem)
  return (
    <ModalTabsOverflowBar
      items={visibleTabs.map((tab) => ({
        id: tab.id,
        label: tab.label,
        icon: tab.icon,
        count: tab.countKey ? counts[tab.countKey] : undefined,
      }))}
      activeId={activeTab}
      onChange={(id) => onTabChange(id as VehicleModalTab)}
    />
  );
}
